/*
 * Isolated per-param URL state management.
 *
 * Problem: useQueryParamState calls useSearchParams() internally, which
 * re-renders every consumer whenever ANY URL param changes — even params
 * they don't care about.
 *
 * Solution: a single QueryParamStoreProvider owns useSearchParams() and
 * syncs URL changes into a functional store that notifies only the
 * subscribers of the param that actually changed.  Components use
 * useQueryState (same API as useQueryParamState) and only
 * re-render when their own param changes.
 */
import qs, { ParsedQs } from 'qs';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { useSearchParams } from 'react-router-dom';

// qs config — must match useQueryParamState for URL compatibility
const QS_PARSE_OPTIONS = {
  arrayLimit: 100,
  ignoreQueryPrefix: true,
  strictNullHandling: true,
} as const;

const QS_STRINGIFY_OPTIONS = {
  strictNullHandling: true,
} as const;

function parseQs(queryString: string): ParsedQs {
  return qs.parse(queryString, QS_PARSE_OPTIONS);
}

function stringifyQs(params: Record<string, any>): string {
  return qs.stringify(params, QS_STRINGIFY_OPTIONS);
}

// ---------------------------------------------------------------------------
// Store (functional, no classes)
// ---------------------------------------------------------------------------

interface QueryParamStore {
  syncFromUrl: (searchString: string) => void;
  subscribeToParam: (name: string, listener: () => void) => () => void;
  getSerializedParam: (name: string) => string | undefined;
  setParam: (name: string, value: any, debounceMs: number) => void;
  bindSetSearchParams: (
    setter: (search: string, options?: { replace?: boolean }) => void,
  ) => void;
}

function createQueryParamStore(): QueryParamStore {
  // Per-param JSON-serialized snapshots (stable strings for useSyncExternalStore)
  const paramSnapshots = new Map<string, string | undefined>();
  // Per-param subscriber sets
  const paramListeners = new Map<string, Set<() => void>>();
  // react-router setter, bound by the provider
  let setSearchParamsFn:
    | ((search: string, options?: { replace?: boolean }) => void)
    | null = null;
  // Debounce timers for URL writes
  const debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // Params with pending local changes not yet flushed to URL
  const pendingParams = new Set<string>();

  /** Read a single param from the current URL (fallback for lazy init). */
  function readParamFromUrl(name: string): string | undefined {
    const parsed = parseQs(window.location.search);
    const raw = parsed[name];
    return raw !== undefined ? JSON.stringify(raw) : undefined;
  }

  /**
   * Sync all subscribed params from the URL string provided by
   * useSearchParams.  Only params whose serialized snapshot actually
   * changed are notified — all others are untouched.
   */
  function syncFromUrl(searchString: string): void {
    const parsed = parseQs(searchString);

    for (const [name, listeners] of paramListeners) {
      // Skip params whose local change hasn't been flushed to URL yet
      if (pendingParams.has(name)) continue;

      const raw = parsed[name];
      const newSnapshot = raw !== undefined ? JSON.stringify(raw) : undefined;
      const oldSnapshot = paramSnapshots.get(name);

      if (newSnapshot !== oldSnapshot) {
        paramSnapshots.set(name, newSnapshot);
        listeners.forEach(l => l());
      }
    }
  }

  /** Subscribe to a single param.  Lazily initialises its snapshot. */
  function subscribeToParam(name: string, listener: () => void): () => void {
    if (!paramListeners.has(name)) {
      paramListeners.set(name, new Set());
    }
    paramListeners.get(name)!.add(listener);

    // Lazy-init snapshot from current URL
    if (!paramSnapshots.has(name)) {
      paramSnapshots.set(name, readParamFromUrl(name));
    }

    return () => {
      paramListeners.get(name)?.delete(listener);
      if (paramListeners.get(name)?.size === 0) {
        paramListeners.delete(name);
      }
    };
  }

  /** Get the JSON-serialized snapshot of a param (called by useSyncExternalStore). */
  function getSerializedParam(name: string): string | undefined {
    if (!paramSnapshots.has(name)) {
      paramSnapshots.set(name, readParamFromUrl(name));
    }
    return paramSnapshots.get(name);
  }

  /**
   * Set a param value.
   * 1. Update the snapshot immediately (optimistic) and notify subscribers.
   * 2. Schedule a debounced flush to the URL.
   */
  function setParam(name: string, value: any, debounceMs: number): void {
    const newSnapshot = value !== undefined ? JSON.stringify(value) : undefined;
    const oldSnapshot = paramSnapshots.get(name);

    if (newSnapshot !== oldSnapshot) {
      paramSnapshots.set(name, newSnapshot);
      paramListeners.get(name)?.forEach(l => l());
    }

    pendingParams.add(name);

    const existing = debounceTimers.get(name);
    if (existing) clearTimeout(existing);

    debounceTimers.set(
      name,
      setTimeout(() => {
        flushToUrl(name, value);
        debounceTimers.delete(name);
        pendingParams.delete(name);
      }, debounceMs),
    );
  }

  /** Write a single param to the URL (merge with current URL state). */
  function flushToUrl(name: string, value: any): void {
    const currentParams = parseQs(window.location.search);
    const oldNormalized = stringifyQs(currentParams);

    currentParams[name] = value;
    const newSearch = stringifyQs(currentParams);

    if (newSearch !== oldNormalized) {
      setSearchParamsFn?.(newSearch, { replace: true });
    }
  }

  /** Bind the react-router setSearchParams function (called by the provider). */
  function bindSetSearchParams(
    setter: (search: string, options?: { replace?: boolean }) => void,
  ): void {
    setSearchParamsFn = setter;
  }

  return {
    syncFromUrl,
    subscribeToParam,
    getSerializedParam,
    setParam,
    bindSetSearchParams,
  };
}

// ---------------------------------------------------------------------------
// React Context & Provider
// ---------------------------------------------------------------------------

// Use a well-known symbol so that, even if this module is loaded multiple times
// (e.g. separate webpack chunks / micro-frontends), all instances share the
// same React context object.
const CONTEXT_SYMBOL = Symbol.for(
  '@agilelab/plugin-wb-platform/QueryParamStoreContext',
);

declare global {
  interface Window {
    [CONTEXT_SYMBOL]?: ReturnType<typeof createContext<QueryParamStore | null>>;
  }
}

function getOrCreateContext() {
  if (!window[CONTEXT_SYMBOL]) {
    window[CONTEXT_SYMBOL] = createContext<QueryParamStore | null>(null);
  }
  return window[CONTEXT_SYMBOL]!;
}

const QueryParamStoreContext = getOrCreateContext();

/**
 * Single owner of useSearchParams().  Children that use useQueryState will
 * NOT re-render when the URL changes — only when their specific param changes.
 *
 * Children are received as a prop, so React's element-identity
 * optimisation prevents them from re-rendering when this provider
 * re-renders due to useSearchParams.
 */
export function QueryParamStoreProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const storeRef = useRef<QueryParamStore | null>(null);

  if (!storeRef.current) {
    storeRef.current = createQueryParamStore();
  }

  const store = storeRef.current;

  // Keep the react-router setter up to date (it may change on re-render)
  store.bindSetSearchParams(setSearchParams);

  // Sync URL → store.  Only changed params are notified.
  const searchString = searchParams.toString();
  // TODO::Marco Svuotare snapshot dei parametri non più presenti nell'url
  useEffect(() => {
    store.syncFromUrl(searchString);
  }, [store, searchString]);

  return (
    <QueryParamStoreContext.Provider value={store}>
      {children}
    </QueryParamStoreContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Per-param URL state hook.  Drop-in replacement for useQueryParamState but
 * only re-renders when the param identified by `stateName` changes.
 *
 * Must be used within a `<QueryParamStoreProvider>`.
 */
export function useQueryState<T extends ParsedQs | undefined | string>(
  stateName: string,
  options?: { debounce?: number; initValue?: T },
): [T, Dispatch<SetStateAction<T>>] {
  const store = useContext(QueryParamStoreContext);

  if (!store) {
    throw new Error(
      'useQueryState must be used within <QueryParamStoreProvider>',
    );
  }

  const debounceTime = options?.debounce ?? 250;
  // Capture initial value once (same semantics as useState(initialValue))
  const initRef = useRef(options?.initValue);
  // Capture URL state at mount to decide whether to flush initValue
  const initialSnapshotRef = useRef(store.getSerializedParam(stateName));

  // If the URL has no value for this param at mount time, write the initValue into the URL
  useEffect(() => {
    if (
      initialSnapshotRef.current === undefined &&
      initRef.current !== undefined
    ) {
      store.setParam(stateName, initRef.current, 0);
    }
    // Intentionally runs only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = useCallback(
    (listener: () => void) => store.subscribeToParam(stateName, listener),
    [store, stateName],
  );

  const getSnapshot = useCallback(
    () => store.getSerializedParam(stateName),
    [store, stateName],
  );

  const serializedValue = useSyncExternalStore(subscribe, getSnapshot);

  const value: T = useMemo(() => {
    if (serializedValue === undefined) {
      return initRef.current as T;
    }
    try {
      const parsed = JSON.parse(serializedValue);
      return (parsed ?? initRef.current) as T;
    } catch {
      return initRef.current as T;
    }
  }, [serializedValue]);

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (action: SetStateAction<T>) => {
      let newValue: T;
      if (typeof action === 'function') {
        const currentSnapshot = store.getSerializedParam(stateName);
        let currentValue: T;
        if (currentSnapshot !== undefined) {
          try {
            const parsed = JSON.parse(currentSnapshot);
            currentValue = (parsed ?? initRef.current) as T;
          } catch {
            currentValue = initRef.current as T;
          }
        } else {
          currentValue = initRef.current as T;
        }
        newValue = (action as (prev: T) => T)(currentValue);
      } else {
        newValue = action;
      }
      store.setParam(stateName, newValue, debounceTime);
    },
    [store, stateName, debounceTime],
  );

  return [value, setValue];
}
